import { Component, DOCUMENT, inject, signal } from '@angular/core';

/**
 * TEMPORARY: floating picker for comparing the designer's brand concepts on
 * the v3 UI. Sets html[data-brand]; the token overrides and logo swap live in
 * src/styles/brand-preview.scss. Remove both once a concept is chosen.
 */
export type BrandId = 'default' | 'c1' | 'c2' | 'c3';

/** `short` replaces `label` below the sm breakpoint so the pill fits a phone. */
export const BRAND_OPTIONS: readonly { id: BrandId; label: string; short: string; swatch: string }[] = [
  { id: 'default', label: 'Current', short: 'Current', swatch: '#0F1011' },
  { id: 'c1', label: 'Concept 1', short: '1', swatch: '#596AFE' },
  { id: 'c2', label: 'Concept 2', short: '2', swatch: '#F9F781' },
  { id: 'c3', label: 'Concept 3', short: '3', swatch: '#051B20' },
];

export const BRAND_STORAGE_KEY = 'cp-brand-preview';

function isBrandId(value: unknown): value is BrandId {
  return BRAND_OPTIONS.some((o) => o.id === value);
}

@Component({
  selector: 'app-brand-switcher',
  standalone: true,
  template: `
    <div
      role="radiogroup"
      aria-label="Brand concept preview"
      class="fixed bottom-5 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-1 rounded-full border border-black/10 bg-white/90 p-1 font-cp text-[12px] text-neutral-700 shadow-lg backdrop-blur dark:border-white/10 dark:bg-neutral-900/90 dark:text-neutral-200"
    >
      @for (option of options; track option.id) {
        <button
          type="button"
          role="radio"
          [attr.aria-checked]="brand() === option.id"
          (click)="select(option.id)"
          [attr.aria-label]="option.label"
          class="flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 transition-colors hover:bg-black/5 aria-checked:bg-black/10 aria-checked:font-semibold dark:hover:bg-white/10 dark:aria-checked:bg-white/15"
        >
          <span
            class="h-3 w-3 rounded-full ring-1 ring-black/20 dark:ring-white/30"
            [style.background]="option.swatch"
          ></span>
          <span class="sm:hidden" aria-hidden="true">{{ option.short }}</span>
          <span class="hidden sm:inline" aria-hidden="true">{{ option.label }}</span>
        </button>
      }
    </div>
  `,
})
export class BrandSwitcherComponent {
  private readonly document = inject(DOCUMENT);
  readonly options = BRAND_OPTIONS;
  readonly brand = signal<BrandId>(this.readStored());

  constructor() {
    this.apply(this.brand());
  }

  select(id: BrandId): void {
    this.brand.set(id);
    this.apply(id);
    try {
      localStorage.setItem(BRAND_STORAGE_KEY, id);
    } catch {
      // Storage blocked (private mode): the choice just won't persist.
    }
  }

  private apply(id: BrandId): void {
    const root = this.document.documentElement;
    if (id === 'default') {
      root.removeAttribute('data-brand');
    } else {
      root.setAttribute('data-brand', id);
    }
  }

  private readStored(): BrandId {
    try {
      const stored = localStorage.getItem(BRAND_STORAGE_KEY);
      return isBrandId(stored) ? stored : 'default';
    } catch {
      return 'default';
    }
  }
}
